/**
 * **平面の紙に「盤の上の矩形」を描く道具**（Issue #523）。
 *
 * 盤の上のものは全部**平面の上の矩形**なので、4 点（左上・右上・右下・左下）を受け取って
 * その中に絵・塗り・文字を置く道具だけを持つ。
 *
 * **透視はここでは扱わない** —— 紙そのものを `matrix3d` で貼るときに CSS が付ける（`quad.js`）。
 * だからここで要るのはアフィン（平行四辺形）まで＝ 2D canvas の `transform` で足りる。
 */

/** 4 点をなぞるパスを引く（塗る・線を引くのは呼ぶ側） */
export function quadPath(ctx, quad) {
  ctx.beginPath();
  quad.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.closePath();
}

/** 4 点の中心 */
export const centerOf = (quad) => [
  (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4,
  (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4,
];

/** 4 点を中心のまわりに `kx` / `ky` 倍に広げる（区画をひと回り大きく描くのに使う） */
export function scaleQuad(quad, kx = 1, ky = kx) {
  const [cx, cy] = centerOf(quad);
  return quad.map(([x, y]) => [cx + (x - cx) * kx, cy + (y - cy) * ky]);
}

/**
 * 4 点の中を「幅 w・高さ h の絵の座標系」にして `fn(w, h)` を呼ぶ。
 *
 * 中では左上が原点・右が +x・下が +y なので、**カードの絵も文字も普通に置ける**
 * （守備表示で 90° 回した 4 点を渡せば、絵ごと寝る）。
 */
export function inQuad(ctx, quad, fn) {
  const [tl, tr, , bl] = quad;
  const ex = [tr[0] - tl[0], tr[1] - tl[1]];
  const ey = [bl[0] - tl[0], bl[1] - tl[1]];
  const w = Math.hypot(ex[0], ex[1]);
  const h = Math.hypot(ey[0], ey[1]);
  if (!(w > 0) || !(h > 0)) return;
  ctx.save();
  ctx.transform(ex[0] / w, ex[1] / w, ey[0] / h, ey[1] / h, tl[0], tl[1]);
  fn(w, h);
  ctx.restore();
}

/** 角丸の矩形のパス（`ctx.roundRect` は環境によって無いので自前で引く） */
export function roundRectPath(ctx, x, y, w, h, r = 0) {
  const k = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.lineTo(x + w - k, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + k);
  ctx.lineTo(x + w, y + h - k);
  ctx.quadraticCurveTo(x + w, y + h, x + w - k, y + h);
  ctx.lineTo(x + k, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - k);
  ctx.lineTo(x, y + k);
  ctx.quadraticCurveTo(x, y, x + k, y);
  ctx.closePath();
}

/**
 * 幅 w・高さ h の箱に絵を `cover` で敷く（CSS の `object-fit: cover` と同じ）。
 * 絵の寸法が読めなければ引き伸ばす。
 */
export function drawCover(ctx, image, w, h) {
  const iw = image?.naturalWidth || image?.videoWidth || image?.width || 0;
  const ih = image?.naturalHeight || image?.videoHeight || image?.height || 0;
  if (!(iw > 0) || !(ih > 0)) { ctx.drawImage(image, 0, 0, w, h); return; }
  const k = Math.max(w / iw, h / ih);
  const dw = iw * k; const dh = ih * k;
  ctx.drawImage(image, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/**
 * **絵を箱に「覆う」で敷く置き方**（Issue #565。CSS の `object-fit: cover` ＋ 合わせる点）。純関数。
 *
 * 絵の中の点 `focus`（絵の幅・高さに対する比。既定は中央）を**箱の中心**に置き、そのうえで箱に隙間が出ない
 * いちばん小さい倍率にする。たとえばマットの絵の「中央の境目」を平面の中央（P1 と P2 の境）に合わせる。
 * 縦横比が合わない分ははみ出す（箱の外は呼ぶ側が切る）。
 *
 * @param {{w: number, h: number}} src 絵の寸法
 * @param {{x: number, y: number, w: number, h: number}} box
 * @param {{x?: number, y?: number}} [focus]
 * @returns {{x: number, y: number, w: number, h: number}} 絵を描く矩形（箱の座標）
 */
export function coverRect(src, box, focus = {}) {
  const fx = Math.min(1, Math.max(0, focus.x ?? 0.5));
  const fy = Math.min(1, Math.max(0, focus.y ?? 0.5));
  const half = (len, f) => Math.max(f > 0 ? (len / 2) / f : 0, f < 1 ? (len / 2) / (1 - f) : 0);
  // 合わせる点の左右（上下）それぞれが箱の半分以上を覆う倍率
  const k = Math.max(half(box.w, fx) / src.w, half(box.h, fy) / src.h, box.w / src.w, box.h / src.h);
  const w = src.w * k; const h = src.h * k;
  return { x: box.x + box.w / 2 - fx * w, y: box.y + box.h / 2 - fy * h, w, h };
}

/**
 * **9 分割で箱に描く切り方**（Issue #565）。純関数。角は絵の縦横比のまま `scale` 倍、辺と中は伸び縮みする
 * ——**ゾーンの枠**のように、どんな大きさ・縦横比の箱でも角の飾りが潰れない。
 *
 * @param {{w: number, h: number}} src 絵の寸法
 * @param {{l: number, t: number, r: number, b: number}} slice 角の大きさ（絵のピクセル）
 * @param {{x: number, y: number, w: number, h: number}} box 描く箱
 * @param {number} scale 絵のピクセル → 箱の座標の倍率（角の大きさ）。角が箱に入らなければ縮める
 * @returns {number[][]} `[sx, sy, sw, sh, dx, dy, dw, dh]` の 9 個（大きさ 0 のものは落とす）
 */
export function nineSliceRects(src, slice, box, scale) {
  const k = Math.min(scale, box.w / (slice.l + slice.r), box.h / (slice.t + slice.b));
  const sx = [0, slice.l, src.w - slice.r, src.w];
  const sy = [0, slice.t, src.h - slice.b, src.h];
  const dx = [box.x, box.x + slice.l * k, box.x + box.w - slice.r * k, box.x + box.w];
  const dy = [box.y, box.y + slice.t * k, box.y + box.h - slice.b * k, box.y + box.h];
  const out = [];
  for (let j = 0; j < 3; j += 1) {
    for (let i = 0; i < 3; i += 1) {
      const sw = sx[i + 1] - sx[i]; const sh = sy[j + 1] - sy[j];
      const dw = dx[i + 1] - dx[i]; const dh = dy[j + 1] - dy[j];
      if (sw > 0 && sh > 0 && dw > 0 && dh > 0) out.push([sx[i], sy[j], sw, sh, dx[i], dy[j], dw, dh]);
    }
  }
  return out;
}

/**
 * 9 分割で描く（`nineSliceRects`）。切れ目に髪の毛ほどの隙間が出ないよう、描く側だけ少し重ねる。
 * 絵の寸法が読めなければ何もしない。
 */
export function drawNineSlice(ctx, image, slice, box, scale) {
  const w = image?.naturalWidth || image?.width || 0;
  const h = image?.naturalHeight || image?.height || 0;
  if (!(w > 0) || !(h > 0)) return;
  const e = 0.35;
  nineSliceRects({ w, h }, slice, box, scale).forEach(([sx, sy, sw, sh, dx, dy, dw, dh]) => {
    ctx.drawImage(image, sx, sy, sw, sh, dx - e, dy - e, dw + e * 2, dh + e * 2);
  });
}

/**
 * 箱の中に収まるまで字を詰めて 1 行で描く（入らなければ「…」）。
 * **測って縮めるだけ**で、折り返しはしない（盤の上の札は 1 行で読ませる）。
 */
export function fitText(ctx, text, w, size) {
  const s = String(text ?? '');
  if (!s) return '';
  ctx.font = `${size}px sans-serif`;
  if (ctx.measureText?.(s)?.width <= w) return s;
  for (let n = s.length - 1; n > 0; n -= 1) {
    const cut = `${s.slice(0, n)}…`;
    if ((ctx.measureText?.(cut)?.width ?? 0) <= w) return cut;
  }
  return '…';
}

/**
 * `#rrggbb` に不透明度を掛けた `rgba(...)`。
 * **`${色}14` のような 8 桁の hex を組み立てない** —— 3 桁の色（`#5cf`）を渡されると黙って壊れる。
 */
export function rgba(hex, alpha = 1) {
  const s = String(hex ?? '').replace('#', '');
  const full = s.length === 3 ? s.split('').map((c) => c + c).join('') : s;
  const n = parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(n)) return `rgba(255, 255, 255, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * **光る縁**（Issue #523。`core/decor` の `glow` の組を canvas に当てる）。
 *
 * にじみは**影ではなく重ね塗り**で出す —— canvas の `shadowBlur` は毎フレーム描き直す紙では
 * 目に見えて重く（GPU の速い経路から外れる）、環境によって出方も違う。
 *
 * 太い線を薄く `glowN` 枚（一番外が `glowW`）重ねてから、`strokeW` の線を乗せる。
 * **外側から内側へ**描くので、重なりの濃さは元の書き方と同じになる。
 *
 * @param ctx 2D コンテキスト
 * @param path 道を引く（毎回呼ぶ。`beginPath` から）
 * @param decor `core/decor::decorOf()` の値 ＋ `color`（線の色）と `alpha`（本線の濃さ）
 */
export function glowStroke(ctx, path, { color, strokeW, glowW = 0, glowA = 0, glowN = 1, alpha = 1 }) {
  const n = Math.max(0, Math.round(glowN));
  if (glowW > strokeW && glowA > 0) {
    for (let i = n; i >= 1; i -= 1) {
      ctx.globalAlpha = glowA;
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeW + (glowW - strokeW) * (i / n);
      path(ctx);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeW;
  path(ctx);
  ctx.stroke();
  ctx.globalAlpha = 1;
}
